// src/store/issues/issuesSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api.service';
import { errorMessage } from '../errorMessage';

export const fetchIssues = createAsyncThunk(
  'issues/fetchIssues',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await apiService.getIssues(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

export const fetchIssueById = createAsyncThunk(
  'issues/fetchIssueById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.getIssue(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

export const createIssue = createAsyncThunk(
  'issues/createIssue',
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiService.createIssue(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

export const updateIssue = createAsyncThunk(
  'issues/updateIssue',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiService.updateIssue(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

export const deleteIssue = createAsyncThunk(
  'issues/deleteIssue',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.deleteIssue(id);
      return id;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

export const addIssueComment = createAsyncThunk(
  'issues/addIssueComment',
  async ({ id, text }, { rejectWithValue }) => {
    try {
      const response = await apiService.addIssueComment(id, { text });
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

const initialState = {
  issues: [],
  currentIssue: null,
  isLoading: false,
  isSaving: false,
  error: null,
  successMessage: null,
};

/** Replace a row in place, so an inline status change does not refetch the
 *  whole list. Returns a new array -- never mutates the one in state. */
const replaceRow = (rows, updated) =>
  rows.map((row) => (row.id === updated.id ? { ...row, ...updated } : row));

const issuesSlice = createSlice({
  name: 'issues',
  initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
    clearSuccessMessage: (state) => { state.successMessage = null; },
    clearCurrentIssue: (state) => { state.currentIssue = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIssues.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchIssues.fulfilled, (state, action) => {
        state.isLoading = false;
        state.issues = action.payload;
      })
      .addCase(fetchIssues.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchIssueById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchIssueById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentIssue = action.payload;
      })
      .addCase(fetchIssueById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(createIssue.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(createIssue.fulfilled, (state, action) => {
        state.isSaving = false;
        state.issues = [action.payload, ...state.issues];
        state.successMessage = `${action.payload.key} logged.`;
      })
      .addCase(createIssue.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })

      .addCase(updateIssue.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateIssue.fulfilled, (state, action) => {
        state.isSaving = false;
        state.issues = replaceRow(state.issues, action.payload);
        if (state.currentIssue?.id === action.payload.id) {
          state.currentIssue = { ...state.currentIssue, ...action.payload };
        }
        state.successMessage = `${action.payload.key} updated.`;
      })
      .addCase(updateIssue.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })

      .addCase(deleteIssue.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(deleteIssue.fulfilled, (state, action) => {
        state.isSaving = false;
        state.issues = state.issues.filter((row) => row.id !== action.payload);
        state.successMessage = 'Ticket deleted.';
      })
      .addCase(deleteIssue.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })

      .addCase(addIssueComment.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(addIssueComment.fulfilled, (state, action) => {
        state.isSaving = false;
        if (state.currentIssue) {
          state.currentIssue = {
            ...state.currentIssue,
            comments: [...(state.currentIssue.comments || []), action.payload],
          };
        }
      })
      .addCase(addIssueComment.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearSuccessMessage, clearCurrentIssue } = issuesSlice.actions;
export default issuesSlice.reducer;
